import {matchEmail} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  buildContactForHandle,
  ContactSourcesEnum,
} from '../../../libs/shared/core/src/models/api/contactModel';
import {MeetingRoomModel} from '../../models/api/meetingRoomModel';
import {
  ParticipantModel,
  ParticipantResponseStatusesEnum,
  ParticipantTypesEnum,
} from '../../models/api/participantModel';
import {RecipientContactMatchModel} from '../../models/api/recipientMatchModel';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {VisualParticipantModel} from '../../models/api/visualParticipantModel';
import {buildCompleteMeetingRoomName} from './meetingRoomHelpers';

export function tryParseVisualParticipantFromText(
  text: string,
  namespace: string = 'global',
): VisualParticipantModel | null {
  const email = matchEmail(text);
  if (!email) {
    return null;
  }

  return {
    namespace,
    contact: buildContactForHandle(ContactSourcesEnum.EMAIL, email),
    type: ParticipantTypesEnum.REQUIRED,
    isOrganizer: false,
    isCreator: false,
    isAttendee: true,
    responseStatus: ParticipantResponseStatusesEnum.PENDING,
  };
}

export function buildVisualParticipantFromMatch(
  namespace: string,
  recipientMatch: RecipientContactMatchModel,
): VisualParticipantModel {
  return {
    contact: {
      uid: recipientMatch.uid,
      source: recipientMatch.source,
      handle: recipientMatch.handle,
      cardIds: {},
    },
    name: recipientMatch.name,
    namespace,
    type: ParticipantTypesEnum.REQUIRED,
    isOrganizer: false,
    isCreator: false,
    isAttendee: true,
    responseStatus: ParticipantResponseStatusesEnum.PENDING,
  };
}

export function buildVisualParticipantFromVisualContact(
  visualContact: VisualContactModel,
): VisualParticipantModel {
  return {
    contact: visualContact.contact,
    name: visualContact.name ?? visualContact.contact.handle,
    namespace: visualContact.namespace,
    type: ParticipantTypesEnum.REQUIRED,
    isOrganizer: false,
    isCreator: false,
    isAttendee: true,
    responseStatus: ParticipantResponseStatusesEnum.PENDING,
  };
}

export function buildVisualParticipantFromMeetingRoom(meetingRoom: MeetingRoomModel): VisualParticipantModel {
  return {
    contact: buildContactForHandle(ContactSourcesEnum.EMAIL, meetingRoom.address),
    name: buildCompleteMeetingRoomName(meetingRoom),
    namespace: 'global',
    type: ParticipantTypesEnum.RESOURCE,
    isOrganizer: false,
    isCreator: false,
    isAttendee: true,
    responseStatus: ParticipantResponseStatusesEnum.PENDING,
  };
}

export function buildParticipantFromVisualParticipant(
  visualParticipant: VisualParticipantModel,
): ParticipantModel {
  return {
    contactUid: visualParticipant.contact.uid,
    type: visualParticipant.type,
    isOrganizer: visualParticipant.isOrganizer,
    isCreator: visualParticipant.isCreator,
    isAttendee: visualParticipant.isAttendee,
    responseStatus: visualParticipant.responseStatus,
    name: visualParticipant.name,
  };
}

export function maybeAddOrganizerToVisualParticipants(
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
  organizerAddress: string | undefined,
) {
  if (!organizerAddress) {
    return visualParticipants;
  }

  const organizerVisualParticipant = tryParseVisualParticipantFromText(organizerAddress);
  if (!organizerVisualParticipant) {
    return visualParticipants;
  }

  const organizerUpdates: Partial<VisualParticipantModel> = {
    isOrganizer: true,
    responseStatus: ParticipantResponseStatusesEnum.ACCEPTED,
  };

  // If a participant already exists for the organizer, update it.
  const existing = visualParticipants.find((p) => p.contact.uid === organizerVisualParticipant.contact.uid);
  if (existing) {
    return visualParticipants.map((p) => {
      if (p === existing) {
        return {
          ...p,
          ...organizerUpdates,
        };
      }

      return p;
    });
  }

  // Otherwise, add it to the collection.
  const updatedOrganizerVisualParticipant = {
    ...organizerVisualParticipant,
    ...organizerUpdates,
  };

  return [updatedOrganizerVisualParticipant, ...visualParticipants];
}
